import { useRef, useEffect, type ReactNode } from 'react';
import { Collapsible, DropdownMenu } from 'radix-ui';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import {
  applyIssueReportOffer,
  buildNavItems,
  resolvePrimaryCta,
  type NavItem,
  type PrimaryCta,
} from '@/lib/constants/navigation';
import { NavDropdown } from './NavDropdown';
import { hasAnyRole } from '@/lib/constants/roles';
import { isAdmin } from '@/lib/auth/admin-access';
import { canAny } from '@/lib/auth/permissions';
import { useAuth } from '@/hooks/useAuth';
import { useLogout } from '@/hooks/useLogout';
import { isViewAsTab } from '@/lib/auth/session';
import { useMobileMenu } from '@/hooks/useMobileMenu';
import { Logo } from '@/components/ui/Logo';
import { Button } from '@/components/ui/Button';
import { ComingSoonBadge } from '@/components/common/ComingSoonBadge';
import { ProfileLogo } from '@/components/common/ProfileLogo';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { clearPurchaseIntent, peekPurchaseIntent } from '@/lib/auth/intent-storage';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { userInitials } from '@/lib/utils/user.utils';
import { useProfile } from '@/hooks/useProfile';

const activeClass = 'text-white';
const inactiveClass = 'text-slate hover:text-white';

/** The mobile menu's button variant per CTA kind, resolved once. "Coming Soon" is not a button. */
const MOBILE_CTA_VARIANT: Record<
  Exclude<PrimaryCta['kind'], 'coming_soon'>,
  'primary' | 'danger' | 'yellow' | 'outline-light'
> = {
  link: 'primary',
  cancel: 'danger',
  leave_journey: 'outline-light',
  billing: 'yellow',
};

export interface AppNavbarProps {
  /**
   * Feature-owned content rendered before the account menu (e.g. the
   * notifications bell, MYN-55). `AppNavbar` lives in `components/layout/`
   * and must not import from `features/` directly - `AppLayout` composes
   * the actual component and passes it in here.
   */
  rightSlot?: ReactNode;
  /**
   * Whether the current user has an in-progress create-startup wizard draft
   * worth resuming (`useHasInProgressStartupDraft`, feature-owned - same
   * reason as `rightSlot`: `AppLayout` composes it in rather than this
   * component importing from `features/` directly). Swaps the primary CTA's
   * label from "Create Startup Profile" to "Resume Startup Profile".
   */
  hasInProgressStartupDraft?: boolean;
}

export function AppNavbar({ rightSlot, hasInProgressStartupDraft = false }: AppNavbarProps) {
  const { user } = useAuth();
  const logout = useLogout();
  // A view-as tab leaves through the banner's Exit, which ends the session:
  // logging out is a write the Backend refuses there, and would end nothing.
  const canLogOut = !isViewAsTab();
  const { pathname } = useLocation();
  const { open, setOpen, containerRef } = useMobileMenu();
  const mobileNavRef = useRef<HTMLElement>(null);

  const roles = user?.roles ?? [];
  // Both gates are AND'd (see NavItem's doc): a role check (identity/persona
  // tier) and, when present, a permission check (AUTHZ-1 capability seam).
  const isVisible = (item: NavItem) =>
    hasAnyRole(roles, item.allowedRoles) &&
    (!item.requiredPermission || canAny(user ?? null, item.requiredPermission)) &&
    (!item.requiresAdmin || isAdmin(user ?? null));
  const navItems = buildNavItems(user?.activeMembership ?? null)
    .filter(isVisible)
    // Dropdown children are gated too (e.g. Team Access requires
    // team.manage inside the Workspace menu), so filter them the same way.
    .map((item) => (item.children ? { ...item, children: item.children.filter(isVisible) } : item));
  // From the cached profile, so renaming yourself updates the bubble at once
  // (UX sheet, StartupAdminVF r86) - never the role or the email.
  const { data: profile } = useProfile();
  const initials = userInitials(profile, user?.email);
  // Primary CTA is resolved centrally (keyed on primary role, not raw
  // membership) so individuals get Create Startup, entity members get the
  // billing upsell, and super admins get neither - see resolvePrimaryCta.
  // Route-aware: on /startup/create the CTA flips to Cancel Startup.
  // While the Issue Report journey is in progress the create form's Cancel is
  // replaced by a way back to the Due Diligence page (FR-205).
  const baseCta = resolvePrimaryCta(
    user,
    pathname,
    hasInProgressStartupDraft,
    peekPurchaseIntent() !== null,
  );
  // Only the Issue Report invitation depends on whether the plan is on sale
  // (FR-203), so only it asks for the offer.
  const { data: offer } = useIssueReportOffer({ enabled: baseCta?.kind === 'billing' });
  const primaryCta = applyIssueReportOffer(baseCta, offer);

  useEffect(() => {
    if (open) {
      mobileNavRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
    }
  }, [open]);

  return (
    <Collapsible.Root
      open={open}
      onOpenChange={setOpen}
      ref={containerRef}
      className="relative shrink-0"
    >
      {/* `h-app-navbar` (= the old `h-20`, 5rem) rather than a bare `h-20`, so
          the full-screen states that size themselves against the remaining
          viewport read the same `--spacing-app-navbar` token this height
          comes from and cannot drift out of sync with it. */}
      <header className="h-app-navbar bg-dashboard-nav flex items-center gap-8 px-6">
        {/* Logo */}
        <NavLink
          to={ROUTES.DASHBOARD}
          className="shrink-0"
          aria-label={`${UI_STRINGS.BRAND_NAME} home`}
        >
          <Logo variant="white" height={36} aria-hidden />
        </NavLink>

        {/* Desktop nav — centered */}
        <nav
          className="hidden flex-1 items-center justify-center gap-6 overflow-x-auto md:flex"
          aria-label="Main navigation"
        >
          {navItems.map((item) =>
            item.children ? (
              <NavDropdown key={item.label} item={item} />
            ) : (
              <NavLink
                key={item.label}
                to={item.to ?? '#'}
                {...(item.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className={({ isActive }) =>
                  `text-base font-medium whitespace-nowrap transition-colors select-none ${
                    isActive ? activeClass : inactiveClass
                  }`
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        {/* Right side — primary CTA + avatar */}
        <div className="hidden flex-shrink-0 items-center gap-6 md:flex">
          {primaryCta?.kind === 'link' && (
            <Button asChild variant="primary">
              <Link to={primaryCta.to}>{primaryCta.label}</Link>
            </Button>
          )}
          {primaryCta?.kind === 'cancel' && (
            <Button asChild variant="danger">
              <Link to={primaryCta.to}>{primaryCta.label}</Link>
            </Button>
          )}
          {primaryCta?.kind === 'leave_journey' && (
            <Button asChild variant="outline-light">
              {/* Leaving the journey forgets it, so the next login lands normally (FR-206). */}
              <Link to={primaryCta.to} onClick={clearPurchaseIntent}>
                {primaryCta.label}
              </Link>
            </Button>
          )}
          {primaryCta?.kind === 'billing' && (
            /* The Issue Report invitation (FR-128): the one purchase
               affordance, replacing "Upgrade to Premium". */
            <Button asChild variant="yellow">
              <Link to={primaryCta.to}>{primaryCta.label}</Link>
            </Button>
          )}
          {/* No Issue Report plan on sale: nothing to press (FR-203). */}
          {primaryCta?.kind === 'coming_soon' && <ComingSoonBadge size="sm" onDark />}

          {rightSlot}

          {/* Account menu: avatar + chevron. No email beside the avatar (UX sheet,
              StartupAdminVF r75): it is shown inside the menu, which the client
              said is enough. */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="flex items-center gap-3 rounded-full select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                aria-label={`${user?.email ?? 'User'} account menu`}
              >
                {/* The uploaded profile photo replaces the initials once there is
                    one (UX round 3, StartupAdminPatty Q29); ProfileLogo falls back to
                    initials itself if the presigned URL fails to load. */}
                {profile?.avatarUrl ? (
                  <ProfileLogo
                    url={profile.avatarUrl}
                    name={`${profile.firstName} ${profile.lastName}`.trim() || initials}
                    size={42}
                    shape="circle"
                  />
                ) : (
                  <span className="bg-teal2 flex h-10.5 w-10.5 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white select-none">
                    {initials}
                  </span>
                )}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-slate"
                  aria-hidden="true"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={8}
                className="rounded-card z-50 min-w-50 bg-white py-1 shadow-lg"
              >
                {user?.email && (
                  <div className="text-slate truncate border-b border-black/5 px-4 py-2 text-sm">
                    {user.email}
                  </div>
                )}
                {canLogOut && (
                  <DropdownMenu.Item
                    onSelect={logout}
                    className="text-navy cursor-pointer px-4 py-2.5 text-sm outline-none hover:bg-black/5 focus:bg-black/5 data-highlighted:bg-black/5"
                  >
                    {UI_STRINGS.NAV.LOG_OUT}
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>

        {/* Mobile hamburger */}
        <div className="ml-auto md:hidden">
          <Collapsible.Trigger asChild>
            <button
              type="button"
              className="rounded-lg p-2 text-white transition-colors hover:bg-white/10"
              aria-label={open ? 'Close menu' : 'Open menu'}
            >
              {open ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
          </Collapsible.Trigger>
        </div>
      </header>

      {/* Mobile menu */}
      <Collapsible.Content>
        <nav
          ref={mobileNavRef}
          className="bg-dashboard-nav absolute top-full right-0 left-0 z-50 border-t border-white/10 shadow-lg md:hidden"
          aria-label="Mobile navigation"
        >
          <div className="flex flex-col gap-1 p-4">
            {navItems
              // Dropdown parents have no route; flatten their children into the list.
              .flatMap((item) => (item.children ? item.children : [item]))
              .map(({ label, to, openInNewTab }) => (
                <NavLink
                  key={label}
                  to={to ?? '#'}
                  {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                      isActive ? `${activeClass} bg-white/5` : `${inactiveClass} hover:bg-white/5`
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}
            {/* Every CTA kind, the Issue Report invitation included (FR-128):
                the mobile menu used to drop the billing CTA entirely. */}
            {primaryCta?.kind === 'coming_soon' && (
              <ComingSoonBadge size="sm" onDark className="mt-1" />
            )}
            {primaryCta && primaryCta.kind !== 'coming_soon' && (
              <Button
                asChild
                variant={MOBILE_CTA_VARIANT[primaryCta.kind]}
                className="mt-1 px-4 py-3 text-center text-base"
              >
                <Link
                  to={primaryCta.to}
                  onClick={() => {
                    if (primaryCta.kind === 'leave_journey') clearPurchaseIntent();
                    setOpen(false);
                  }}
                >
                  {primaryCta.label}
                </Link>
              </Button>
            )}
            {/* Raw button (mnr-standards "reuse-or-create" narrow exception): dark
                mobile-nav chrome styled as a nav link, matching the sibling nav
                items; no Button variant fits the dark bar. */}
            {canLogOut && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  logout();
                }}
                className={`rounded-lg px-4 py-3 text-left text-base font-medium transition-colors ${inactiveClass} hover:bg-white/5`}
              >
                {UI_STRINGS.NAV.LOG_OUT}
              </button>
            )}
          </div>
        </nav>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
